import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components';
import {
  Repossession,
  RepossessionDecision,
  RepossessionOpsRecommendation,
  RepossessionRecoveryCostType,
  RepossessionSettlementType,
  RepossessionStatus,
  RepossessionStatusHistoryApi,
} from '@/types/common';
import {
  getRepossessionById,
  getRepossessionStatusHistory,
  closeRepossessionCase,
  escalateRepossessionToManager,
  escalateRepossessionToPotentialLoss,
  makeRepossessionDecision,
  markRepossessionAsFoundNew,
  processRepossessionSettlement,
  startRepossessionInvestigation,
} from '@/services/api/repossessions';
import {
  RepoDetailContent,
  RepoDetailHeader,
  EscalateToManagerModal,
  InvestigationPopUp,
  MarkAsFoundModal,
  MakeDecisionModal,
  PotentialLossConfirmModal,
  StatusNotification,
  StatusTimeline,
  ProcessSettlementModal,
} from './components';
import { getUserEmail } from '@/utils';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';

interface RepoDetailPageProps {}

const RepoDetailPage: React.FC<RepoDetailPageProps> = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [repoCase, setRepoCase] = useState<Repossession | null>(null);
  const [statusHistory, setStatusHistory] = useState<
    RepossessionStatusHistoryApi[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [isInvestigationOpen, setIsInvestigationOpen] = useState(false);
  const [isStartingInvestigation, setIsStartingInvestigation] = useState(false);
  const [isPotentialLossOpen, setIsPotentialLossOpen] = useState(false);
  const [isEscalatingPotentialLoss, setIsEscalatingPotentialLoss] =
    useState(false);
  const [isEscalateManagerOpen, setIsEscalateManagerOpen] = useState(false);
  const [isEscalatingManager, setIsEscalatingManager] = useState(false);
  const [isDecisionOpen, setIsDecisionOpen] = useState(false);
  const [isMakingDecision, setIsMakingDecision] = useState(false);
  const [isMarkAsFoundOpen, setIsMarkAsFoundOpen] = useState(false);
  const [isMarkingAsFound, setIsMarkingAsFound] = useState(false);
  const [isProcessSettlementOpen, setIsProcessSettlementOpen] = useState(false);
  const [isProcessingSettlement, setIsProcessingSettlement] = useState(false);
  const [isClosingCase, setIsClosingCase] = useState(false);

  useEffect(() => {
    if (slug) {
      fetchRepoDetail(slug);
    }
  }, [slug]);

  useEffect(() => {
    if (repoCase?.status !== RepossessionStatus.IN_PROGRESS) return undefined;

    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, [repoCase?.status]);

  const fetchRepoDetail = async (caseId: string) => {
    setLoading(true);
    try {
      const [repoDetail, history] = await Promise.all([
        getRepossessionById(caseId),
        getRepossessionStatusHistory(caseId),
      ]);

      const sortedHistory = history.sort(
        (a, b) =>
          new Date(b.createdAt || '').getTime() -
          new Date(a.createdAt || '').getTime()
      );

      setRepoCase(repoDetail);
      setStatusHistory(sortedHistory);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to load repo case details';
      showErrorToast('Error', message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusAction = (actionType: string) => {
    // TODO: Implement status change modals
    if (actionType === 'Start Investigation') {
      setIsInvestigationOpen(true);
      return;
    }

    if (actionType === 'Mark as Potensi Kehilangan') {
      setIsPotentialLossOpen(true);
      return;
    }

    if (actionType === 'Escalate to Manager') {
      setIsEscalateManagerOpen(true);
      return;
    }

    if (actionType === 'Make Decision') {
      setIsDecisionOpen(true);
      return;
    }

    if (actionType === 'Mark as Found') {
      setIsMarkAsFoundOpen(true);
      return;
    }

    if (actionType === 'Process Settlement') {
      setIsProcessSettlementOpen(true);
      return;
    }

    if (actionType === 'Close Case') {
      handleCloseCase();
      return;
    }

    showSuccessToast('Success', `${actionType} action triggered`);
  };

  const handleStartInvestigation = async (reason: string) => {
    if (!repoCase) return;
    setIsStartingInvestigation(true);
    try {
      const response = await startRepossessionInvestigation(repoCase.ID, {
        reason,
        updatedBy: getUserEmail(),
        escalationHours: 48,
      });

      showSuccessToast(
        'Success',
        response?.message || 'Status updated to IN_PROGRESS'
      );
      setIsInvestigationOpen(false);
      fetchRepoDetail(repoCase.ID);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to start investigation';
      showErrorToast('Error', message);
    } finally {
      setIsStartingInvestigation(false);
    }
  };

  const handleEscalateToPotentialLoss = async () => {
    if (!repoCase) return;
    setIsEscalatingPotentialLoss(true);
    try {
      const response = await escalateRepossessionToPotentialLoss(repoCase.ID, {
        updatedBy: getUserEmail(),
      });

      showSuccessToast(
        'Success',
        response?.message || 'Status updated to POTENTIAL_LOSS'
      );
      setIsPotentialLossOpen(false);
      fetchRepoDetail(repoCase.ID);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to mark as Potensi Kehilangan';
      showErrorToast('Error', message);
    } finally {
      setIsEscalatingPotentialLoss(false);
    }
  };

  const handleEscalateToManager = async (payload: {
    searchEfforts: string;
    escalationReason: string;
    lastKnownInfo: string;
    opsRecommendation: RepossessionOpsRecommendation;
    estimatedCost?: number;
  }) => {
    if (!repoCase) return;
    setIsEscalatingManager(true);
    try {
      const response = await escalateRepossessionToManager(repoCase.ID, {
        ...payload,
        updatedBy: getUserEmail(),
      });

      showSuccessToast('Success', response?.message || 'Escalated to Manager');
      setIsEscalateManagerOpen(false);
      fetchRepoDetail(repoCase.ID);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to escalate to manager';
      showErrorToast('Error', message);
    } finally {
      setIsEscalatingManager(false);
    }
  };

  const handleMakeDecision = async (payload: {
    decision: RepossessionDecision;
    decisionNotes: string;
  }) => {
    if (!repoCase) return;
    setIsMakingDecision(true);
    try {
      const response = await makeRepossessionDecision(repoCase.ID, {
        ...payload,
        updatedBy: getUserEmail(),
      });

      showSuccessToast('Success', response?.message || 'Decision submitted');
      setIsDecisionOpen(false);
      fetchRepoDetail(repoCase.ID);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to submit decision';
      showErrorToast('Error', message);
    } finally {
      setIsMakingDecision(false);
    }
  };

  const handleMarkAsFound = async (payload: {
    recoveryCost: number;
    recoveryCostType?: RepossessionRecoveryCostType;
    recoveryCostTypeOther?: string;
    recoveryNotes: string;
  }) => {
    if (!repoCase) return;
    setIsMarkingAsFound(true);
    try {
      const response = await markRepossessionAsFoundNew(repoCase.ID, {
        ...payload,
        updatedBy: getUserEmail(),
      });

      showSuccessToast(
        'Success',
        response?.message || 'Vehicle marked as found'
      );
      setIsMarkAsFoundOpen(false);
      fetchRepoDetail(repoCase.ID);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to mark as found';
      showErrorToast('Error', message);
    } finally {
      setIsMarkingAsFound(false);
    }
  };

  const handleProcessSettlement = async (payload: {
    settlementAmount: number;
    settlementType: RepossessionSettlementType;
    settlementNotes: string;
    settlementDocumentURL: string;
    financeApproval: boolean;
  }) => {
    if (!repoCase) return;
    setIsProcessingSettlement(true);
    try {
      const response = await processRepossessionSettlement(repoCase.ID, {
        ...payload,
        updatedBy: getUserEmail(),
      });

      showSuccessToast('Success', response?.message || 'Settlement processed');
      setIsProcessSettlementOpen(false);
      fetchRepoDetail(repoCase.ID);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to process settlement';
      showErrorToast('Error', message);
    } finally {
      setIsProcessingSettlement(false);
    }
  };

  const handleCloseCase = async () => {
    if (!repoCase) return;
    setIsClosingCase(true);
    try {
      const response = await closeRepossessionCase(repoCase.ID, {
        updatedBy: getUserEmail(),
      });

      showSuccessToast('Success', response?.message || 'Case closed');
      fetchRepoDetail(repoCase.ID);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to close case';
      showErrorToast('Error', message);
    } finally {
      setIsClosingCase(false);
    }
  };

  const getDecisionOptions = () => {
    if (repoCase?.status !== RepossessionStatus.PENDING_APPROVAL) {
      return [
        RepossessionDecision.REJECT_CONTINUE_SEARCH,
        RepossessionDecision.APPROVE_RECOVERY,
      ];
    }

    switch (repoCase?.escalationType) {
      case 'POTENTIAL_LOSS':
        return [
          RepossessionDecision.REJECT_CONTINUE_SEARCH,
          RepossessionDecision.APPROVE_TOTAL_LOSS,
        ];
      case 'APPROVE_COST':
        return [
          RepossessionDecision.REJECT_CONTINUE_SEARCH,
          RepossessionDecision.APPROVE_CONTINUE_SEARCH,
        ];
      case 'RECOVERY_COST':
      default:
        return [
          RepossessionDecision.REJECT_CONTINUE_SEARCH,
          RepossessionDecision.APPROVE_RECOVERY,
        ];
    }
  };

  const getActionButton = () => {
    if (!repoCase) return null;

    switch (repoCase.status) {
      case RepossessionStatus.OPEN:
        return (
          <Button
            onClick={() => handleStatusAction('Start Investigation')}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            Start Investigation
          </Button>
        );
      case RepossessionStatus.IN_PROGRESS:
        return (
          <div className="flex gap-3">
            <Button
              onClick={() => handleStatusAction('Mark as Potensi Kehilangan')}
              className="rounded-xl bg-red-500 px-6 py-2 text-white hover:bg-red-600"
              variant={'danger'}
            >
              Mark as Potensi Kehilangan
            </Button>
            <Button
              onClick={() => handleStatusAction('Mark as Found')}
              className="rounded-xl bg-purple-600 px-6 py-2 text-white hover:bg-purple-700"
            >
              Mark as Found
            </Button>
          </div>
        );
      case RepossessionStatus.FOUND:
        return (
          <Button
            onClick={() => handleStatusAction('Close Case')}
            disabled={isClosingCase}
            className="bg-gray-600 hover:bg-gray-700 text-white"
          >
            {isClosingCase ? 'Closing...' : 'Close Case'}
          </Button>
        );

      case RepossessionStatus.PENDING_APPROVAL:
        return (
          <Button
            onClick={() => handleStatusAction('Make Decision')}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Make Decision
          </Button>
        );

      case RepossessionStatus.POTENTIAL_LOSS:
        return (
          <div className="flex gap-3">
            <Button
              onClick={() => handleStatusAction('Escalate to Manager')}
              className="rounded-xl bg-red-500 px-6 py-2 text-white hover:bg-red-600"
              variant={'danger'}
            >
              Escalate to Manager
            </Button>
            <Button
              onClick={() => handleStatusAction('Mark as Found')}
              className="rounded-xl bg-purple-600 px-6 py-2 text-white hover:bg-purple-700"
              variant={'primary'}
            >
              Mark as Found
            </Button>
          </div>
        );
      case RepossessionStatus.WRITTEN_OFF:
        return (
          <Button
            onClick={() => handleStatusAction('Process Settlement')}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Process Settlement
          </Button>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">
            Loading repo case details...
          </p>
        </div>
      </div>
    );
  }

  if (!repoCase) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="w-24 h-24 mx-auto mb-4 text-gray-400">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Repo Case Not Found
          </h1>
          <p className="text-gray-600 mb-6">
            The repo case with ID "{slug}" could not be found or may have been
            deleted.
          </p>
          <Button
            onClick={() => navigate('/repossessions')}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            ← Back to Repo List
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto bg-white px-6 py-5">
      <div className="mx-auto">
        <RepoDetailHeader
          repoCase={repoCase}
          onBack={() => navigate('/repossessions')}
          actionButton={getActionButton()}
        />
        <div className="mb-4">
          <StatusNotification repoCase={repoCase} now={now} />
        </div>

        <InvestigationPopUp
          isOpen={isInvestigationOpen}
          repoCase={repoCase}
          onClose={() => setIsInvestigationOpen(false)}
          onSubmit={handleStartInvestigation}
          isSubmitting={isStartingInvestigation}
        />
        <PotentialLossConfirmModal
          isOpen={isPotentialLossOpen}
          onClose={() => setIsPotentialLossOpen(false)}
          onConfirm={handleEscalateToPotentialLoss}
          isSubmitting={isEscalatingPotentialLoss}
        />
        <EscalateToManagerModal
          isOpen={isEscalateManagerOpen}
          onClose={() => setIsEscalateManagerOpen(false)}
          onSubmit={handleEscalateToManager}
          isSubmitting={isEscalatingManager}
        />
        <MakeDecisionModal
          isOpen={isDecisionOpen}
          onClose={() => setIsDecisionOpen(false)}
          onSubmit={handleMakeDecision}
          allowedDecisions={getDecisionOptions()}
          recoveryCost={repoCase?.recoveryCost ?? null}
          isSubmitting={isMakingDecision}
        />
        <MarkAsFoundModal
          isOpen={isMarkAsFoundOpen}
          onClose={() => setIsMarkAsFoundOpen(false)}
          onSubmit={handleMarkAsFound}
          isSubmitting={isMarkingAsFound}
        />
        <ProcessSettlementModal
          isOpen={isProcessSettlementOpen}
          onClose={() => setIsProcessSettlementOpen(false)}
          onSubmit={handleProcessSettlement}
          isSubmitting={isProcessingSettlement}
        />
        {/* Main Content */}
        <div className="flex flex-col gap-2 lg:flex-row lg:justify-between">
          {/* Left Column - Case Details */}
          <div className="w-full basis-[60%] flex-shrink-0 min-w-0">
            <RepoDetailContent repoCase={repoCase} />
          </div>

          {/* Right Column - Status Timeline (match screenshot) */}
          <div className="w-full basis-[40%] flex-shrink-0 min-w-0">
            <StatusTimeline statusHistory={statusHistory} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepoDetailPage;
